import React, { useRef, useState, useEffect } from "react";
import { Button } from "@clarkinc/zest-design-system";
import { cls } from "@wss/cls";
import { ReCaptchaProvider } from "@wss/re-captcha";
import { ProductDetailGroupingSelect } from "~/ProductDetails/ProductDetailPageResult";
import ReviewVgItemSelectionModal from "../../ReviewVgItemSelectionModal";
import useIsMounted from "~/common/useIsMounted";
import DialogModal from "~/ViewCart/DialogModal";
import CustomerReviewForm from "~/my-account/CustomerReview/CustomerReviewForm";
import { User } from "~/common/Store/models/User";
import { homepage } from "~/common/Constants/Constants";
import loginModalStore from "~/common/LoginModal/loginModalStore";
interface Props {
  user: User;
  productDetailGroupingSelect: ProductDetailGroupingSelect[];
  handleVirtualGroupingSelectChange?: (itemNumber: string) => string;
  isVirtualGroupMaster: boolean;
  itemNumber: string;
  reviewSubmissionHandler: (reviewSubmitted: boolean) => void;
  displayExtended: boolean;
  image: string;
  description: string;
  productLink?: string;
  loggedInWithModal: boolean;
  setLoggedInWithModal: (loggedInWithModal: boolean) => void;
}
const LeaveAReviewButton = ({
  user,
  productDetailGroupingSelect,
  handleVirtualGroupingSelectChange,
  isVirtualGroupMaster,
  itemNumber,
  reviewSubmissionHandler,
  displayExtended,
  image,
  description,
  productLink = homepage,
  loggedInWithModal,
  setLoggedInWithModal
}: Props) => {
  const mounted = useIsMounted();
  const [openVgModal, setOpenVgModal] = useState(false);
  const reviewModalRef = useRef<HTMLDialogElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const handleVgSelectionClick = () => {
    setOpenVgModal(true);
  };
  const toggleVgModal = (modalOpen: boolean) => {
    setOpenVgModal(modalOpen);
    if (!modalOpen) {
      setLoggedInWithModal(false);
    }
  };
  const leaveReviewHandler = () => {
    if (!reviewModalRef.current?.open) {
      reviewModalRef.current?.showModal();
      setIsOpen(true);
    }
  };
  const closeModal = () => {
    reviewModalRef.current?.close();
  };
  const registeredOnClickHandler = isVirtualGroupMaster ? handleVgSelectionClick : leaveReviewHandler;
  const unRegisteredOnClickHandler = () => {
    loginModalStore.openLogin({
      targetUrl: window.location.pathname,
      callback: () => {
        setLoggedInWithModal(true);
        loginModalStore.closeLogin();
      }
    });
  };
  useEffect(() => {
    if (loggedInWithModal && user.registered && !openVgModal && reviewModalRef.current) {
      registeredOnClickHandler();
      setLoggedInWithModal(false);
    }
  }, [loggedInWithModal, registeredOnClickHandler, user.registered, openVgModal, setLoggedInWithModal]);
  const dataTestId = isVirtualGroupMaster ? "review-selectVg-button" : "review-button";
  const reviewFormStyles = {
    formStyles: cls("mx-0"),
    modalDivStyles: cls("px-6", "md:px-8", "overflow-y-auto", "max-h-[min(calc(100vh-271px),634px)]", "h-[634px]", "md:max-h-[min(calc(100vh-271px),587px)]", "md:[h-587px]"),
    ratingLegendStyles: cls("text-base", "mb-1"),
    ratingStyles: cls("mb-px"),
    ratingErrorStyles: cls("w-full"),
    reviewHeadingStyles: cls("mb-0", "text-base"),
    reviewSubStyles: cls("mt-0.5", "mb-0", "w-full"),
    textAreaStyles: cls("w-full", "box-border", "mt-3", "h-[90px]", "md:h-[110px]"),
    fileHeadingStyles: cls("mt-6", "text-base"),
    fileSubStyles: cls("mb-3", "text-sm", "w-full"),
    footerStyles: cls("bg-gray-100", "p-6"),
    buttonsStyles: cls("xxs:flex-row"),
    cancelButtonStyles: cls("xxs:mb-0"),
    submitButtonStyles: cls("xxs:ml-4"),
    disclaimerStyles: cls("xl:mt-0", "mt-4")
  };
  return <ReCaptchaProvider data-sentry-element="ReCaptchaProvider" data-sentry-component="LeaveAReviewButton" data-sentry-source-file="LeaveAReviewButton.tsx">
      <ReviewVgItemSelectionModal productDetailGroupingSelect={productDetailGroupingSelect} handleVirtualGroupingSelectChange={handleVirtualGroupingSelectChange} setOpenModal={toggleVgModal} openModal={openVgModal} isVirtualGroupMaster={isVirtualGroupMaster} itemNumber={itemNumber} leaveReviewHandler={leaveReviewHandler} user={user} data-sentry-element="ReviewVgItemSelectionModal" data-sentry-source-file="LeaveAReviewButton.tsx" />
      <Button theme="wss" type="button" variant="green" size="medium" aria-label="Write Review" id="writeReviewButton" className={`${displayExtended ? "lg:w-auto" : ""} font-normal w-full`} data-testid={user.registered ? dataTestId : "review-button-logged-out"} onClick={user.registered ? registeredOnClickHandler : unRegisteredOnClickHandler} data-sentry-element="Button" data-sentry-source-file="LeaveAReviewButton.tsx">
        {user.registered ? "Write your own review" : "Login or Register"}
      </Button>

      {mounted && <DialogModal modalRef={reviewModalRef} headerTitle="Review this product" className="md:max-w-[680px] border-gray-400 rounded-lg backdrop:bg-gray-900 backdrop:bg-opacity-60 overflow-y-hidden" addMainPadding={false} headerStyles={{
      lineHeight: "28px",
      fontSize: "20px",
      paddingTop: "16px",
      paddingBottom: "16px"
    }} closeModal={() => {
      closeModal();
      setIsOpen(false);
    }}>
          <CustomerReviewForm productId={itemNumber} cancelForm={closeModal} setSuccessMessage={reviewSubmissionHandler} products={[]} isInModal image={image} description={description} productLink={productLink} styles={reviewFormStyles} displayExtended={displayExtended} isOpen={isOpen} />
        </DialogModal>}
    </ReCaptchaProvider>;
};
export default LeaveAReviewButton;